import produce from 'immer';
import { AsyncStatus } from 'lib-shared-legacy-types';
import { Queue } from 'lib-shared-types-api';
import { Action, ActionMeta, handleActions } from 'redux-actions';

import { ResultMetaT } from 'utils/sagaApiGeneratorHelpers';

import { transferToQueueActions } from '../actions/transferToQueueModal';

type Request = {
  csid: number;
  requestToQueue: Queue;
};

type RequestPayload = Request & {
  timeout?: number;
};

type RequestPayloadMeta = ActionMeta<
  TransferToQueueModalState,
  ResultMetaT<Request>
>;

type WsResponsePayload = {
  csid: number;
  accepted: boolean;
  cause: 'rejected' | 'takeback' | 'timeout' | 'accepted'; // TODO (TEAMS-3919): consolidate between other transferTo modals
};

export type TransferToQueueModalState = {
  meta: {
    asyncStatus: AsyncStatus;
  };
  ui: {
    isModalShown: boolean;
  };
  csid: number | null;
  requests: Array<RequestPayload>;
};

const initialState: TransferToQueueModalState = {
  meta: {
    asyncStatus: 'succeeded',
  },
  ui: {
    isModalShown: false,
  },
  csid: null,
  requests: [],
};

export const {
  transferToQueueModalRequest,
  transferToQueueModalRequestSucceeded,
  transferToQueueModalRequestFailed,
  transferToQueueModalRequestStop,

  transferToQueueWsResponse,
  transferToQueueWsTransferStarted,
  transferToQueueWsTransferAnswered,
} = transferToQueueActions;

export const transferToQueue = handleActions<TransferToQueueModalState, any>(
  {
    [transferToQueueModalRequest.toString()]: (
      state,
      action: Action<RequestPayload>,
    ) => {
      const { csid, requestToQueue } = action.payload;

      return produce(state, (draft) => {
        draft.meta.asyncStatus = 'loading';
        draft.requests = [
          ...draft.requests.filter((request) => request.csid !== csid),
          { csid, requestToQueue, timeout: 3100 },
        ];
      });
    },

    [transferToQueueModalRequestSucceeded.toString()]: (
      state,
      action: Action<RequestPayloadMeta>,
    ) => {
      const { csid } = action.payload.meta.request;
      const index = state.requests.findIndex(
        (request) => request.csid === csid,
      );

      return produce(state, (draft) => {
        draft.meta.asyncStatus = 'succeeded';

        if (index > -1) {
          draft.requests = draft.requests.filter(
            (request) => request.csid !== csid,
          );
        }
      });
    },

    [transferToQueueModalRequestFailed.toString()]: (
      state,
      action: Action<RequestPayload>,
    ) => {
      const { csid } = action.payload;
      const index = state.requests.findIndex(
        (request) => request.csid === csid,
      );

      return produce(state, (draft) => {
        if (index > -1) {
          draft.meta.asyncStatus = 'failed';
          draft.requests = draft.requests.filter(
            (request) => request.csid !== csid,
          );
        }
      });
    },

    [transferToQueueModalRequestStop.toString()]: (
      state,
      action: Action<RequestPayload>,
    ) => {
      const { csid } = action.payload;
      const index = state.requests.findIndex(
        (request) => request.csid === csid,
      );

      return produce(state, (draft) => {
        if (index > -1) {
          draft.meta.asyncStatus = 'failed';
          draft.requests = draft.requests.filter(
            (request) => request.csid !== csid,
          );
        }
      });
    },

    [transferToQueueWsResponse.toString()]: (
      state,
      action: Action<WsResponsePayload>,
    ) => {
      const { csid, accepted } = action.payload;
      const index = state.requests.findIndex(
        (request) =>
          request.csid === csid && (accepted === true || accepted === false),
      );

      return produce(state, (draft) => {
        if (index > -1) {
          draft.requests = draft.requests.filter(
            (request) => request.csid !== csid,
          );
        }
      });
    },
  },
  initialState,
);
